import { type ConversationResponse } from "@vega-api/generated/types/conversationResponse";
import { NotificationCenterEmptyState } from "@vega-core/components/NotificationCenter/components/NotificationCenterEmptyState";
import { NotificationCenterLoadMoreSentinel } from "@vega-core/components/NotificationCenter/components/NotificationCenterLoadMoreSentinel";
import { m } from "@vega-core/paraglide/messages";

import { ConversationListItem } from "./ConversationListItem";
import { useInfiniteConversations } from "./conversationsData";
import { MessagingErrorState } from "./MessagingErrorState";

type Props = {
  metaFilter?: Record<string, string>;
  listTestId: string;
  errorTestId: string;
  buildItemTestId: (conversation: ConversationResponse) => string;
  buildConversationPath: (conversation: ConversationResponse) => string;
  getParticipantName?: (conversation: ConversationResponse) => string;
};

export const ConversationsList = ({
  metaFilter,
  listTestId,
  errorTestId,
  buildItemTestId,
  buildConversationPath,
  getParticipantName,
}: Props) => {
  const listQuery = useInfiniteConversations({ metaFilter });

  const conversations =
    listQuery.data?.pages.flatMap((page) => page.data) ?? [];

  if (listQuery.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="skeleton-box h-20 rounded-lg" />
        <div className="skeleton-box h-20 rounded-lg" />
        <div className="skeleton-box h-20 rounded-lg" />
      </div>
    );
  }

  if (listQuery.isError && conversations.length === 0) {
    return (
      <MessagingErrorState
        isRetrying={listQuery.isRefetching}
        onRetry={() => listQuery.refetch()}
        testId={errorTestId}
      />
    );
  }

  if (conversations.length === 0) {
    return (
      <NotificationCenterEmptyState
        description={m.coInvestments_messages_empty_subtitle()}
        title={m.coInvestments_messages_empty_title()}
      />
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-3" data-testid={listTestId}>
        {conversations.map((conversation) => (
          <li key={conversation.id}>
            <ConversationListItem
              conversation={conversation}
              participantName={getParticipantName?.(conversation)}
              testId={buildItemTestId(conversation)}
              to={buildConversationPath(conversation)}
            />
          </li>
        ))}
      </ul>
      {listQuery.hasNextPage ? (
        <NotificationCenterLoadMoreSentinel
          isLoadingMore={listQuery.isFetchingNextPage}
          loadingLabel={m.common_loading()}
          onLoadMore={() => listQuery.fetchNextPage()}
        />
      ) : null}
    </>
  );
};
